import { useId } from 'react';

import { NumberField } from '@/components/ui/number-field';

/** Three characters is the whole domain: headings and angles are whole degrees in [0, 360], so the
 * longest legitimate entry is "360". Capping the field rather than validating after the fact means a
 * fourth keystroke simply never lands, which is a clearer signal than an error state appearing. */
const BYPASS_MAX_CHARS = 3;

/** Shared by the input and its inert twin on the unselected card, so the two cannot drift apart —
 * the display state is meant to be the active state with nothing clickable, and a second copy of
 * these classes is how that stops being true. */
const BYPASS_BOX_CLASS = 'w-16 rounded border bg-ws-btn py-1.5 pl-2 pr-5 text-sm';

/**
 * The student-facing manual answer box: the fallback a teacher enables so a student whose sensor is
 * missing, uncalibrated or hopeless can still answer.
 *
 * Shared by Compass and Angle. Both had their own static-span placeholder deferred to "the student
 * runner", and both need identical behaviour, so this is one component rather than two that agree
 * today. GPS deliberately has no bypass — a coordinate cannot be meaningfully typed by a student
 * standing in a field, which is the whole point of the block.
 *
 * Label beside the field, not inside it: the field holds at most three characters, far too narrow to
 * show "Enter Manually" as placeholder text, and a placeholder would vanish the moment a student
 * started typing — taking the only explanation of what the box is with it. `flex-wrap` lets the pair
 * drop to two lines on a narrow card instead of overflowing.
 */
export function SensorManualBypass({
  label, quantityLabel, value, onChange, invalid, invalidMessage, inert,
}: {
  /** The visible label, shared across blocks ("Enter Manually"). */
  label: string;
  /** Appended to the accessible name, sr-only, to say WHICH quantity this block wants ("heading in
   * degrees"). The visible label alone is ambiguous on a page holding several sensor blocks. Kept as
   * a suffix on the real label rather than an `aria-label` so the accessible name still CONTAINS the
   * visible text, which `aria-label` would have replaced outright. */
  quantityLabel: string;
  value: string;
  onChange: (v: string) => void;
  /** Non-blank text that isn't a degree value in range. */
  invalid: boolean;
  /** Shown when `invalid`. Unlike the teacher-facing simulate strip, which highlights and says
   * nothing, this box is a STUDENT's only way to answer: a bare red border tells them something is
   * wrong but not what, and the value they typed is refused rather than silently corrected. */
  invalidMessage: string;
  /** The unselected canvas card: same markup, nothing interactive. A real input cannot be used there
   * because that card mounts this inside its own activation button, and nested interactive elements
   * are invalid HTML that browsers may reparent. */
  inert?: boolean;
}) {
  const fieldId = useId();
  const errorId = useId();
  return (
    <div className='flex flex-col items-center gap-1'>
    <div className='flex flex-wrap items-center justify-center gap-2'>
      <label htmlFor={inert ? undefined : fieldId} className='text-sm text-ws-muted'>
        {label}
        <span className='sr-only'> {quantityLabel}</span>
      </label>
      {/* The degree sign is pinned inside the box and always visible, rather than appended to the
          value or the placeholder: it has to survive both the empty state and typing. */}
      <span className='relative shrink-0'>
        {inert ? (
          <span className={`${BYPASS_BOX_CLASS} block border-ws-border text-ws-muted opacity-50`}>&nbsp;</span>
        ) : (
          <NumberField
            grammar='decimal'
            id={fieldId}
            value={value}
            onChange={onChange}
            hasError={invalid}
            describedBy={invalid ? errorId : undefined}
            maxLength={BYPASS_MAX_CHARS}
            className={BYPASS_BOX_CLASS}
          />
        )}
        <span
          aria-hidden='true'
          className='pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-sm text-ws-muted'
        >
          °
        </span>
      </span>
    </div>
      {invalid && !inert && (
        <p id={errorId} className='text-xs text-ws-error-fg'>{invalidMessage}</p>
      )}
    </div>
  );
}
